import { Money } from '../ui/Money';
import React from 'react';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Banknote } from 'lucide-react';

const EmployeeCostsSection = ({ formData, setFormData }) => {
  const handleCostChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      employee_costs: {
        ...prev.employee_costs,
        [field]: parseFloat(value) || 0
      }
    }));
  };

  const costs = formData.employee_costs || {};
  
  // Get ticket frequency from flight_ticket settings (default to 2_years)
  const ticketFrequency = formData.flight_ticket?.ticket_frequency || '2_years';
  const isOneYear = ticketFrequency === '1_year';
  const ticketDivisor = isOneYear ? 12 : 24;

  // Calculate monthly breakdowns
  const visaMonthly = (costs.visa_cost || 0) / 24;
  const insuranceMonthly = (costs.insurance_cost || 0) / 12;
  const uniformMonthly = (costs.uniform_cost || 0) / 12;
  const ticketMonthly = (costs.ticket_budget || 0) / ticketDivisor;
  const accommodationMonthly = costs.accommodation_cost || 0;
  
  const totalMonthlyOverhead = visaMonthly + insuranceMonthly + uniformMonthly + ticketMonthly + accommodationMonthly;

  return (
    <Card className="border-orange-200">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg text-orange-700">
          <Banknote className="h-5 w-5" />
          Employee Costs (Overhead Expenses)
        </CardTitle>
        <p className="text-sm text-gray-500">Enter total amounts - monthly breakdown is auto-calculated</p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {/* Visa Cost */}
          <div className="space-y-2">
            <Label htmlFor="visa_cost">Visa Cost (2-Year Total)</Label>
            <Input
              id="visa_cost"
              type="number"
              step="0.01"
              value={costs.visa_cost || ''}
              onChange={(e) => handleCostChange('visa_cost', e.target.value)}
              placeholder="e.g., 3000"
              data-testid="visa-cost-input"
            />
            <p className="text-xs text-gray-500">Monthly: <Money value={visaMonthly} digits={2} /></p>
          </div>

          {/* Insurance Cost */}
          <div className="space-y-2">
            <Label htmlFor="insurance_cost">Insurance (Annual)</Label>
            <Input
              id="insurance_cost"
              type="number"
              step="0.01"
              value={costs.insurance_cost || ''}
              onChange={(e) => handleCostChange('insurance_cost', e.target.value)}
              placeholder="e.g., 1200"
              data-testid="insurance-cost-input"
            />
            <p className="text-xs text-gray-500">Monthly: <Money value={insuranceMonthly} digits={2} /></p>
          </div>

          {/* Uniform Cost */}
          <div className="space-y-2">
            <Label htmlFor="uniform_cost">Uniform (Annual)</Label>
            <Input
              id="uniform_cost"
              type="number"
              step="0.01"
              value={costs.uniform_cost || ''}
              onChange={(e) => handleCostChange('uniform_cost', e.target.value)}
              placeholder="e.g., 500"
              data-testid="uniform-cost-input"
            />
            <p className="text-xs text-gray-500">Monthly: <Money value={uniformMonthly} digits={2} /></p>
          </div>

          {/* Accommodation Cost */}
          <div className="space-y-2">
            <Label htmlFor="accommodation_cost">Accommodation (Monthly)</Label>
            <Input
              id="accommodation_cost"
              type="number"
              step="0.01"
              value={costs.accommodation_cost || ''}
              onChange={(e) => handleCostChange('accommodation_cost', e.target.value)}
              placeholder="e.g., 800"
              data-testid="accommodation-cost-input"
            />
            <p className="text-xs text-gray-500">Monthly: <Money value={accommodationMonthly} digits={2} /></p>
          </div>

          {/* Ticket Budget - Dynamic based on ticket frequency */}
          <div className="space-y-2">
            <Label htmlFor="ticket_budget">
              Ticket Budget ({isOneYear ? '1-Year' : '2-Year'} Total)
              {isOneYear && <span className="ml-1 text-xs text-blue-600">(1Y)</span>}
            </Label>
            <Input
              id="ticket_budget"
              type="number"
              step="0.01"
              value={costs.ticket_budget || ''}
              onChange={(e) => handleCostChange('ticket_budget', e.target.value)}
              placeholder="e.g., 2000"
              data-testid="ticket-budget-input"
            />
            <p className="text-xs text-gray-500">
              Monthly: <Money value={ticketMonthly} digits={2} />
              <span className="text-gray-400 ml-1">
                (÷{ticketDivisor})
              </span>
            </p>
          </div>

          {/* Total Monthly Overhead */}
          <div className="space-y-2 bg-orange-50 p-3 rounded-lg">
            <Label className="text-orange-700 font-semibold">Total Monthly Overhead</Label>
            <p className="text-2xl font-bold text-orange-600"><Money value={totalMonthlyOverhead} digits={2} /></p>
            <p className="text-xs text-gray-500">Added to salary for cost to company</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default EmployeeCostsSection;
